'use client'

import React from 'react'
import Link from 'next/link'

import { useSegmentCode } from '@rewardops/auth/src/client/use-segment-code'
import { buttonVariants, Divider } from '@rewardops/ui'
import { cn } from '@rewardops/ui/src/lib/utils'
import { localeFromLocaleStr, type Mapping } from '@rewardops/utils'

import { useTranslation } from '~/app/i18n/client'
import { useApi } from './hooks/use-api'

interface FooterItemProps {
  className?: string
  children?: React.ReactNode | React.ReactNode[]
  href: string
}

const FooterItem: React.FC<FooterItemProps> = ({
  children,
  className,
  href,
}: FooterItemProps) => {
  return (
    <Link
      href={href}
      className={cn(
        buttonVariants({ variant: 'link' }),
        'text-base text-white hover:text-white',
        className,
      )}
    >
      {children}
    </Link>
  )
}

interface FooterProps {
  localeStr: string
  className?: string
  children?: React.ReactNode | React.ReactNode[]
}

const Footer: React.FC<FooterProps> = ({
  className,
  localeStr,
}: FooterProps) => {
  const { api } = useApi()
  const locale = localeFromLocaleStr(localeStr)
  const { t } = useTranslation(undefined, undefined, locale)
  const segmentCode = useSegmentCode()

  const { data: mapping }: { data: Mapping | undefined } =
    api.contentful.getById.useQuery({
      key: segmentCode.toUpperCase(),
      language: localeStr,
    })

  const localizedHref = (href: string) => `/${localeStr}${href}`

  const getTermsHref = () => {
    const urlOrCmsId = mapping?.value1

    if (urlOrCmsId?.toLowerCase().startsWith('http')) {
      return urlOrCmsId
    }

    return localizedHref('/terms-and-conditions')
  }

  return (
    <footer className={cn(' bg-gray-800 text-white', className)}>
      <div className="container max-w-screen-xl overflow-hidden py-20 sm:px-6 lg:px-8">
        <FooterItem href={localizedHref('/faq')} className="pl-0">
          {t('footer.faq')}
        </FooterItem>
        {'|'}
        <FooterItem href={getTermsHref()}>{t('footer.terms')}</FooterItem>
        {'|'}
        <FooterItem href={mapping?.value2 || 'https://www.rewardops.com/'}>
          {t('footer.privacy')}
        </FooterItem>
        <Divider className="my-4" />
        <p className="text-xs">
          Copyright © {new Date().getFullYear()} RewardOps Inc.{' '}
          {t('footer.allRightsReserved')}
        </p>
      </div>
    </footer>
  )
}

export { Footer }
